import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowDown, FaEnvelope, FaGithub, FaLinkedin } from 'react-icons/fa';
import { CONTACT_INFO } from '../constants/contact';

const HeroSection = styled.section`
  min-height: 94vh;
  display: flex;
  align-items: center;
  padding: 140px 0 84px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 112px 0 64px;
  }
`;

const HeroLayout = styled.div`
  width: 100%;
  z-index: 2;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.72fr);
  gap: 4rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const HeroContent = styled.div`
  max-width: 760px;
`;

const Greeting = styled(motion.h1)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.82rem;
  color: #80e6ff;
  margin-bottom: 1.15rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
  padding: 0.48rem 0.8rem;
  border: 1px solid rgba(128, 230, 255, 0.22);
  border-radius: 999px;
  background: rgba(128, 230, 255, 0.08);
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Name = styled(motion.h2)`
  font-size: clamp(3.2rem, 8vw, 6.6rem);
  line-height: 0.95;
  font-weight: 800;
  margin-bottom: 1.25rem;
  background: linear-gradient(135deg, #ffffff 0%, #9beeff 46%, #7affc8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled(motion.h3)`
  font-size: clamp(1.35rem, 3vw, 2.25rem);
  color: #f6fbff;
  margin-bottom: 1.5rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    line-height: 1.35;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.15rem;
  color: #aebdcb;
  margin-bottom: 2rem;
  line-height: 1.85;
  max-width: 690px;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const MetricRow = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
  margin: 0 0 2rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Metric = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.045);
`;

const MetricValue = styled.strong`
  display: block;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.2;
`;

const MetricLabel = styled.span`
  color: #8fa2b5;
  font-size: 0.84rem;
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.85rem;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(128, 230, 255, 0.28);
  border-radius: 8px;
  color: #80e6ff;
  font-size: 1.15rem;
  background: rgba(255, 255, 255, 0.045);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background: rgba(128, 230, 255, 0.14);
    border-color: rgba(128, 230, 255, 0.58);
    transform: translateY(-3px);
  }
`;

const ActionRow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CTAButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  background: linear-gradient(135deg, #7affc8, #67d8ff);
  color: #061016;
  border: none;
  padding: 0.95rem 1.25rem;
  font-size: 1rem;
  font-weight: 800;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 16px 42px rgba(103, 216, 255, 0.2);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 48px rgba(122, 255, 200, 0.22);
  }
`;

const ProfilePanel = styled(motion.aside)`
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.25rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.26);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(135deg, rgba(122, 255, 200, 0.18), transparent 38%, rgba(103, 216, 255, 0.15));
    pointer-events: none;
  }
`;

const PanelTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1.1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
`;

const Avatar = styled.img`
  width: 76px;
  height: 76px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.16);
`;

const PanelName = styled.div`
  color: #ffffff;
  font-size: 1.15rem;
  font-weight: 800;
`;

const PanelRole = styled.div`
  color: #8fa2b5;
  font-size: 0.9rem;
`;

const TimelineList = styled.ul`
  position: relative;
  z-index: 1;
  list-style: none;
  display: grid;
  gap: 0.85rem;
`;

const TimelineItem = styled.li`
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 1rem;
  color: #dbe8f3;
  font-size: 0.94rem;

  span {
    color: #7affc8;
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    gap: 0.2rem;
  }
`;

const Hero: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id="hero">
      <HeroLayout>
        <HeroContent>
          <Greeting initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}>
            Available for backend · frontend · mobile
          </Greeting>
          <Name initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}>
            김수빈
          </Name>
          <Title initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}>
            서비스를 끝까지 이어 붙이는 Full-Stack Developer
          </Title>
          <Description initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}>
            Spring Boot와 React를 중심으로 API, 데이터베이스, 화면 흐름을 함께 설계합니다.
            모바일과 배포까지 필요한 범위를 빠르게 연결하고, 팀이 계속 고칠 수 있는 구조를 남기는 데 집중합니다.
          </Description>
          <MetricRow initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75, ease: "easeOut" }}>
            <Metric>
              <MetricValue>10+</MetricValue>
              <MetricLabel>GitHub 기반 프로젝트</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>API + App</MetricValue>
              <MetricLabel>서버와 화면 동시 구현</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>AI · Realtime</MetricValue>
              <MetricLabel>OpenAI와 WebSocket 경험</MetricLabel>
            </Metric>
          </MetricRow>
          <ActionRow initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9, ease: "easeOut" }}>
            <CTAButton onClick={() => scrollToSection('projects')} whileTap={{ scale: 0.95 }}>
              프로젝트 보기 <FaArrowDown />
            </CTAButton>
            <SocialLinks>
              <SocialLink href={CONTACT_INFO.GITHUB} target="_blank" rel="noopener noreferrer" whileTap={{ scale: 0.95 }} aria-label="GitHub">
                <FaGithub />
              </SocialLink>
              <SocialLink href={CONTACT_INFO.LINKEDIN} target="_blank" rel="noopener noreferrer" whileTap={{ scale: 0.95 }} aria-label="LinkedIn">
                <FaLinkedin />
              </SocialLink>
              <SocialLink href={`mailto:${CONTACT_INFO.EMAIL}`} whileTap={{ scale: 0.95 }} aria-label="Email">
                <FaEnvelope />
              </SocialLink>
            </SocialLinks>
          </ActionRow>
        </HeroContent>
        <ProfilePanel initial={{ opacity: 0, x: 35 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.55, ease: "easeOut" }}>
          <PanelTop>
            <Avatar src="/IMG_3228.jpeg" alt="김수빈 프로필 사진" />
            <div>
              <PanelName>Kim Subin</PanelName>
              <PanelRole>Spring · React · Flutter</PanelRole>
            </div>
          </PanelTop>
          <TimelineList>
            <TimelineItem><span>Backend</span><p>Spring Boot 도메인 API, JWT, JPA, Swagger 문서화</p></TimelineItem>
            <TimelineItem><span>Realtime</span><p>STOMP WebSocket 기반 채팅·협업 이벤트 처리</p></TimelineItem>
            <TimelineItem><span>Mobile</span><p>Flutter/React Native 지도, 알림, 로컬 저장, EAS 빌드</p></TimelineItem>
            <TimelineItem><span>AI</span><p>OpenAI 챗봇, 추천, 꿈 해석, 음성·이미지 분석 실험</p></TimelineItem>
          </TimelineList>
        </ProfilePanel>
      </HeroLayout>
    </HeroSection>
  );
};

export default Hero;
